/**
 * Module-level bridge between the `apiRequest` client and `AuthProvider`.
 *
 * Purpose
 * ───────
 * `api/client.ts` must trigger a token refresh when it receives 401 and a
 * retry, but it cannot import React context (the client is callable from
 * non-React code, e.g. SSR-like utilities, jest workers).
 *
 * `AuthProvider` registers its refresh callback here on mount; the client
 * looks it up at runtime. There is intentionally **one** bridge per
 * Vite app (admin, vendor) and one per Expo app (mobile).
 *
 * Public API
 * ──────────
 *   setRefreshHandler(fn)  – called by AuthProvider once it has refresh
 *                            tokens to swap; pass `null` on logout.
 *   triggerRefresh()       – called by api/client.ts on 401. Returns the
 *                            new access token on success, or `null` when
 *                            no handler is registered / refresh failed.
 *   getAccessToken()       – snapshot of the current access token, used
 *                            by the retry leg of api/client.ts.
 *   setAccessToken(t)      – AuthProvider keeps this in sync with state.
 */

type RefreshHandler = () => Promise<string | null>;

let refreshHandler: RefreshHandler | null = null;
let accessToken: string | null = null;
let inFlight: Promise<string | null> | null = null;

export function setRefreshHandler(fn: RefreshHandler | null): void {
  refreshHandler = fn;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export async function triggerRefresh(): Promise<string | null> {
  if (!refreshHandler) return null;
  if (!inFlight) {
    inFlight = refreshHandler().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
